/**
 * GENI Multi-Language System
 * Lightweight i18n with English (default) and Portuguese (PT-BR)
 * 
 * IMPORTANT: Trading keywords are intentionally kept in English:
 * - LONG / SHORT
 * - RR (Risk/Reward)
 * - PnL (Profit and Loss)
 * - Win Rate, Profit Factor, Drawdown
 * - Asset symbols (BTC, ETH, etc.)
 */

export const translations = {
  en: {
    // Main Navigation
    nav: {
      dashboard: 'Dashboard',
      openPositions: 'Open Positions',
      history: 'History',
      analytics: 'Analytics',
      advanced: 'Advanced',
      performance: 'Performance',
      alerts: 'Alerts',
      about: 'About',
      settings: 'Settings',
      admin: 'Admin Panel',
    },

    // User Menu
    menu: {
      becomePro: 'Become Pro',
      demoAccount: 'Demo Account',
      exitDemo: 'Exit Demo',
      billing: 'Billing',
      referral: 'Referral',
      settings: 'Settings',
      tradeClassification: 'Trade Classification',
      takeTour: 'Take tour again',
      termsOfUse: 'Terms of Use',
      privacyPolicy: 'Privacy Policy',
      support: 'Support',
      report: 'Report',
      logout: 'Logout',
      language: 'Language',
      viewingDemo: 'Viewing Demo Account',
      tryLabel: 'TRY',
      proLabel: 'PRO',
    },

    // Common UI
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      search: 'Search',
      filter: 'Filter',
      clearAll: 'Clear All',
      clearFilter: 'Clear Filter',
      noData: 'No data available',
      viewAll: 'View All',
      seeDetails: 'See Details',
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      startTracking: 'Start tracking',
      login: 'Login',
      signUp: 'Sign up',
      or: 'or',
      yes: 'Yes',
      no: 'No',
      today: 'Today',
      week: 'Week',
      month: 'Month',
      year: 'Year',
      all: 'All',
      total: 'Total',
      average: 'Average',
      recent: 'Recent',
      previous: 'Previous',
      since: 'Since',
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Your trading performance at a glance',
      quickStats: 'Quick Stats',
      recentTrades: 'Recent Trades',
      uploadChart: 'Upload Chart',
      pasteChart: 'Paste or drag a chart image',
      analyzing: 'Analyzing chart...',
      analyzingTrades: 'Analyzing your trades...',
      noTrades: 'No trades yet',
      startByUploading: 'Start by uploading your first chart',
      addTrade: 'Add Trade',
      manualEntry: 'Manual Entry',
      capitalManagement: 'Capital Management',
      startingCapital: 'Starting Capital',
      currentCapital: 'Current Capital',
      totalReturn: 'Total Return',
      bestDay: 'Best Day',
      worstDay: 'Worst Day',
      // Upload section
      uploadToExtract: 'Upload a trading chart to extract data',
      pasteOrDrag: 'Paste or drag a chart image, or click to upload',
      uploadAndAnalyze: 'Upload and analyze a chart to see results',
      // Insights section
      tradingInsights: 'Trading Insights',
      basedOnYourData: 'Based on your data',
      noInsightsYet: 'No insights yet',
      addMoreTrades: 'Add more trades to generate personalized insights',
      minTradesRequired: 'Minimum 10 trades required for insights',
      // Stats
      trades: 'trades',
      wins: 'wins',
      losses: 'losses',
      closedTrades: 'Closed Trades',
      avgRR: 'Avg RR',
      // Trade limit
      tradeLimit: 'Trade Limit',
      tradesRemaining: 'trades remaining',
      limitReached: 'Limit reached',
      upgradeForUnlimited: 'Upgrade to Pro for unlimited trades',
      // Manual trade
      createManualTrade: 'Create Manual Trade',
      manualTradeDesc: 'Add a trade entry manually',
      // Result
      extractedData: 'Extracted Data',
      confirmTrade: 'Confirm Trade',
      editBeforeSaving: 'Edit any values before saving',
    },

    // Open Positions
    openPositions: {
      title: 'Open Positions',
      subtitle: 'Manage your active trades',
      noOpenPositions: 'No open positions',
      noOpenPositionsDesc: 'Upload a chart or create a manual trade to see open positions here',
      currentPnL: 'Current P&L',
      unrealized: 'Unrealized',
      closePosition: 'Close Position',
      partialClose: 'Partial Close',
      addPartial: 'Add Partial',
      moveToBreakeven: 'Move to Breakeven',
      tradeDetails: 'Trade Details',
      // Hedge
      addHedge: 'Add Hedge',
      editHedge: 'Edit Hedge',
      recordHedge: 'Record Hedge',
      hedgeResult: 'Hedge Result (USD)',
      hedgeNote: 'Note (optional)',
      hedgeHint: 'Positive = hedge helped • Negative = hedge hurt',
      removeHedge: 'Remove',
      saveHedge: 'Save Hedge',
      updateHedge: 'Update Hedge',
    },

    // History
    history: {
      title: 'Trade History',
      subtitle: 'trades analyzed',
      allTrades: 'All Trades',
      tradeDetails: 'Trade Details',
      selectTrade: 'Select a trade to view details',
      calendar: 'Calendar',
      noTradesYet: 'No trades analyzed yet',
      uploadFirst: 'Upload and analyze your first chart to get started',
      showingTradesFor: 'Showing trades for',
      clearFilter: 'Clear filter',
      notTriggered: 'Not Triggered',
      // Filters and sorting
      sortBy: 'Sort by',
      filterBy: 'Filter by',
      newest: 'Newest',
      oldest: 'Oldest',
      highestRR: 'Highest RR',
      lowestRR: 'Lowest RR',
      winsOnly: 'Wins only',
      lossesOnly: 'Losses only',
      // Trade card labels
      entry: 'Entry',
      exit: 'Exit',
      stopLoss: 'Stop Loss',
      takeProfit: 'Take Profit',
      notes: 'Notes',
      setup: 'Setup',
      confidence: 'Confidence',
      outcome: 'Outcome',
      win: 'Win',
      loss: 'Loss',
      open: 'Open',
      pending: 'Pending',
      // Actions
      deleteTrade: 'Delete Trade',
      editTrade: 'Edit Trade',
      viewChart: 'View Chart',
    },

    // Analytics
    analytics: {
      title: 'Analytics',
      subtitle: 'Deep dive into your performance',
      advancedTitle: 'Advanced Analytics',
      advancedSubtitle: 'Multi-dimensional performance analysis',
      filters: 'Filters',
      setup: 'Setup',
      allSetups: 'All Setups',
      assetGroup: 'Asset Group',
      allGroups: 'All Groups',
      timeframe: 'Timeframe',
      allTimeframes: 'All Timeframes',
      tradeType: 'Trade Type',
      allTypes: 'All Types',
      noMatchingTrades: 'No trades match the selected filters',
      performanceByTimeframe: 'Performance by Timeframe',
      performanceByAssetGroup: 'Performance by Asset Group',
      tradeTypeComparison: 'Trade Type Comparison',
      basedOnSettings: 'Based on your settings',
      editThresholds: 'Edit thresholds',
      noTradesClassified: 'No trades classified yet',
      startAddingTrades: 'Start adding trades with timeframes to see your Scalp, Day Trade, and Swing performance breakdown.',
      // Section titles
      overallPerformance: 'Overall Performance',
      performanceMetrics: 'Performance Metrics',
      setupPerformance: 'Setup Performance',
      assetPerformance: 'Asset Performance',
      behaviorAnalysis: 'Behavior Analysis',
      // Labels
      totalTrades: 'Total Trades',
      winningTrades: 'Winning Trades',
      losingTrades: 'Losing Trades',
      avgWin: 'Avg Win',
      avgLoss: 'Avg Loss',
      largestWin: 'Largest Win',
      largestLoss: 'Largest Loss',
      consecutiveWins: 'Consecutive Wins',
      consecutiveLosses: 'Consecutive Losses',
      totalR: 'TOTAL R',
      tradesPerMonth: 'trades/month',
      rPerMonth: 'R/month',
      monthlyPerformance: 'MONTHLY PERFORMANCE',
      monthColumn: 'MONTH',
      operationsColumn: 'TRADES',
      currentMonth: 'Current month',
      monthlyDrilldownHint: 'Click a month to view its trades',
      // Empty states
      noTradesInPeriod: 'No trades in this period',
      adjustFilters: 'Try adjusting your filters',
      noSetupData: 'No setup data available',
      noAssetData: 'No asset data available',
    },

    // Trade Types (kept short, these are labels)
    tradeTypes: {
      scalp: 'Scalp',
      dayTrade: 'Day Trade',
      swingTrade: 'Swing Trade',
      noScalpTrades: 'No scalp trades',
      noDayTrades: 'No day trades',
      noSwingTrades: 'No swing trades',
      shortestDuration: 'Shortest duration trades',
      mediumDuration: 'Medium duration trades',
      longestDuration: 'Longest duration trades',
    },

    // Alerts
    alerts: {
      title: 'Alerts',
      subtitle: 'Stay on top of your trading rules',
      noAlerts: 'No alerts configured',
      createFirst: 'Create your first alert to monitor important thresholds',
      createAlert: 'Create Alert',
      editAlert: 'Edit Alert',
      alertName: 'Alert Name',
      condition: 'Condition',
      threshold: 'Threshold',
      activeAlerts: 'Active Alerts',
      triggeredAlerts: 'Triggered Alerts',
      alertHistory: 'Alert History',
      riskControl: 'Risk Control',
      performanceMonitor: 'Performance Monitor',
      behaviorTracking: 'Behavior Tracking',
      // Alert types
      maxDailyLoss: 'Max Daily Loss',
      maxWeeklyLoss: 'Max Weekly Loss',
      winRateThreshold: 'Win Rate Threshold',
      profitTarget: 'Profit Target',
      maxConsecutiveLosses: 'Max Consecutive Losses',
      maxTradesPerDay: 'Max Trades Per Day',
    },

    // Insights
    insights: {
      title: 'Insights',
      recentRRWarning: 'Recent RR Warning',
      winningRRWarning: 'Winning RR Warning',
      losingStreak: 'Losing Streak',
      performanceDropping: 'Your recent performance is dropping',
      reviewStrategy: 'Review your strategy and consider taking a break',
      limitedData: 'Limited data',
      notEnoughData: 'Not enough data to generate a reliable decision yet',
      improvement: 'improvement',
      decline: 'decline',
      // Additional insight types
      bestSetup: 'Best Setup',
      worstSetup: 'Worst Setup',
      strongTimeframe: 'Strong Timeframe',
      weakTimeframe: 'Weak Timeframe',
      overtrading: 'Overtrading Alert',
      goodStreak: 'Good Streak',
      badStreak: 'Bad Streak',
      consistency: 'Consistency',
      // Descriptions
      yourBestPerforming: 'Your best performing',
      considerstopping: 'Consider stopping for today',
      keepItUp: 'Keep it up!',
      takeABreak: 'Consider taking a break',
    },

    // Settings
    settings: {
      title: 'Settings',
      tradeClassification: 'Trade Classification',
      tradeClassificationDesc: 'Define how trades are classified based on duration',
      scalpMaxDuration: 'Scalp Max Duration',
      dayTradeMaxDuration: 'Day Trade Max Duration',
      minutes: 'minutes',
      swingTradeNote: 'Trades longer than Day Trade max will be classified as Swing Trade',
      saveChanges: 'Save Changes',
      savingChanges: 'Saving...',
      changesSaved: 'Settings saved successfully',
      reclassifyNote: 'All existing trades will be reclassified based on new thresholds',
    },

    // Performance
    performance: {
      title: 'Performance',
      subtitle: 'Track your growth over time',
      equityCurve: 'Equity Curve',
      monthlyBreakdown: 'Monthly Breakdown',
      weeklyBreakdown: 'Weekly Breakdown',
      dailyBreakdown: 'Daily Breakdown',
      tradingDays: 'Trading Days',
      profitableDays: 'Profitable Days',
      losingDays: 'Losing Days',
      breakEvenDays: 'Break Even Days',
      // Additional
      cumulativeReturn: 'Cumulative Return',
      dailyReturn: 'Daily Return',
      weeklyReturn: 'Weekly Return',
      monthlyReturn: 'Monthly Return',
      maxDrawdown: 'Max Drawdown',
      currentDrawdown: 'Current Drawdown',
      recoveryFactor: 'Recovery Factor',
      sharpeRatio: 'Sharpe Ratio',
      // Time periods
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      allTime: 'All Time',
      thisMonth: 'This Month',
      thisWeek: 'This Week',
      // Stats
      bestMonth: 'Best Month',
      worstMonth: 'Worst Month',
      avgMonthlyReturn: 'Avg Monthly Return',
      positiveMonths: 'Positive Months',
      negativeMonths: 'Negative Months',
    },

    // Behavior Analytics
    behavior: {
      partialExecution: 'Partial Execution',
      partialAnalysis: 'Partial Analysis',
      plannedTrades: 'Planned Trades',
      executionQuality: 'Execution Quality',
      notTriggeredTrades: 'Not Triggered Trades',
      followedPlan: 'Followed Plan',
      holdTime: 'Hold Time',
      avgHoldTime: 'Avg Hold Time',
      expectedHoldTime: 'Expected Hold Time',
      partialDecision: 'Partial vs Non-Partial',
      givenBack: 'Given Back',
      firstPartial: 'First Partial',
      finalRealized: 'Final Realized',
    },

    // Errors & Messages
    messages: {
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Info',
      somethingWentWrong: 'Something went wrong',
      tryAgain: 'Please try again',
      savedSuccessfully: 'Saved successfully',
      deletedSuccessfully: 'Deleted successfully',
      loadingFailed: 'Failed to load data',
      connectionError: 'Connection error',
      sessionExpired: 'Session expired',
      pleaseLogin: 'Please log in to continue',
      confirmDelete: 'Are you sure you want to delete this?',
      actionCannotBeUndone: 'This action cannot be undone',
    },

    // Pro/Upgrade
    pro: {
      becomePro: 'Become Pro',
      unlockFeatures: 'Unlock all features',
      currentPlan: 'Current Plan',
      freePlan: 'Free Plan',
      proPlan: 'Pro Plan',
      tradeLimit: 'Trade Limit',
      unlimited: 'Unlimited',
      upgradeNow: 'Upgrade Now',
      monthlyPlan: 'Monthly',
      yearlyPlan: 'Yearly',
      perMonth: '/month',
      perYear: '/year',
    },

    // Footer
    footer: {
      builtBy: 'Built by',
      allRightsReserved: 'All rights reserved',
    },
  },

  ptBR: {
    // Main Navigation
    nav: {
      dashboard: 'Dashboard',
      openPositions: 'Posições Abertas',
      history: 'Histórico',
      analytics: 'Análises',
      advanced: 'Avançado',
      performance: 'Performance',
      alerts: 'Alertas',
      about: 'Sobre',
      settings: 'Configurações',
      admin: 'Painel Admin',
    },

    // User Menu
    menu: {
      becomePro: 'Seja Pro',
      demoAccount: 'Conta Demo',
      exitDemo: 'Sair da Demo',
      billing: 'Cobrança',
      referral: 'Indicação',
      settings: 'Configurações',
      tradeClassification: 'Classificação de Trade',
      takeTour: 'Ver tour novamente',
      termsOfUse: 'Termos de Uso',
      privacyPolicy: 'Política de Privacidade',
      support: 'Suporte',
      report: 'Reportar problema',
      logout: 'Sair',
      language: 'Idioma',
      viewingDemo: 'Visualizando Conta Demo',
      tryLabel: 'TESTAR',
      proLabel: 'PRO',
    },

    // Common UI
    common: {
      loading: 'Carregando...',
      save: 'Salvar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      delete: 'Excluir',
      edit: 'Editar',
      add: 'Adicionar',
      close: 'Fechar',
      back: 'Voltar',
      next: 'Próximo',
      submit: 'Enviar',
      search: 'Buscar',
      filter: 'Filtro',
      clearAll: 'Limpar Tudo',
      clearFilter: 'Limpar Filtro',
      noData: 'Nenhum dado disponível',
      viewAll: 'Ver Todos',
      seeDetails: 'Ver Detalhes',
      learnMore: 'Saiba Mais',
      getStarted: 'Começar',
      startTracking: 'Começar a rastrear',
      login: 'Entrar',
      signUp: 'Criar conta',
      or: 'ou',
      yes: 'Sim',
      no: 'Não',
      today: 'Hoje',
      week: 'Semana',
      month: 'Mês',
      year: 'Ano',
      all: 'Todos',
      total: 'Total',
      average: 'Média',
      recent: 'Recente',
      previous: 'Anterior',
      since: 'Desde',
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Sua performance de trading em um olhar',
      quickStats: 'Estatísticas Rápidas',
      recentTrades: 'Trades Recentes',
      uploadChart: 'Enviar Gráfico',
      pasteChart: 'Cole ou arraste uma imagem do gráfico',
      analyzing: 'Analisando gráfico...',
      analyzingTrades: 'Analisando seus trades...',
      noTrades: 'Nenhum trade ainda',
      startByUploading: 'Comece enviando seu primeiro gráfico',
      addTrade: 'Adicionar Trade',
      manualEntry: 'Entrada Manual',
      capitalManagement: 'Gestão de Capital',
      startingCapital: 'Capital Inicial',
      currentCapital: 'Capital Atual',
      totalReturn: 'Retorno Total',
      bestDay: 'Melhor Dia',
      worstDay: 'Pior Dia',
      // Upload section
      uploadToExtract: 'Envie um gráfico de trading para extrair dados',
      pasteOrDrag: 'Cole ou arraste uma imagem, ou clique para enviar',
      uploadAndAnalyze: 'Envie e analise um gráfico para ver os resultados',
      // Insights section
      tradingInsights: 'Insights de Trading',
      basedOnYourData: 'Baseado nos seus dados',
      noInsightsYet: 'Nenhum insight ainda',
      addMoreTrades: 'Adicione mais trades para gerar insights personalizados',
      minTradesRequired: 'Mínimo de 10 trades necessários para insights',
      // Stats
      trades: 'trades',
      wins: 'wins',
      losses: 'losses',
      closedTrades: 'Trades Fechados',
      avgRR: 'RR Médio',
      // Trade limit
      tradeLimit: 'Limite de Trades',
      tradesRemaining: 'trades restantes',
      limitReached: 'Limite atingido',
      upgradeForUnlimited: 'Faça upgrade para Pro para trades ilimitados',
      // Manual trade
      createManualTrade: 'Criar Trade Manual',
      manualTradeDesc: 'Adicione uma entrada de trade manualmente',
      // Result
      extractedData: 'Dados Extraídos',
      confirmTrade: 'Confirmar Trade',
      editBeforeSaving: 'Edite os valores antes de salvar',
    },

    // Open Positions
    openPositions: {
      title: 'Posições Abertas',
      subtitle: 'Gerencie seus trades ativos',
      noOpenPositions: 'Nenhuma posição aberta',
      noOpenPositionsDesc: 'Envie um gráfico ou crie um trade manual para ver posições abertas aqui',
      currentPnL: 'P&L Atual',
      unrealized: 'Não realizado',
      closePosition: 'Fechar Posição',
      partialClose: 'Fechamento Parcial',
      addPartial: 'Adicionar Parcial',
      moveToBreakeven: 'Mover para Breakeven',
      tradeDetails: 'Detalhes do Trade',
      // Hedge
      addHedge: 'Adicionar Hedge',
      editHedge: 'Editar Hedge',
      recordHedge: 'Registrar Hedge',
      hedgeResult: 'Resultado do Hedge (USD)',
      hedgeNote: 'Nota (opcional)',
      hedgeHint: 'Positivo = hedge ajudou • Negativo = hedge prejudicou',
      removeHedge: 'Remover',
      saveHedge: 'Salvar Hedge',
      updateHedge: 'Atualizar Hedge',
    },

    // History
    history: {
      title: 'Histórico de Trades',
      subtitle: 'trades analisados',
      allTrades: 'Todos os Trades',
      tradeDetails: 'Detalhes do Trade',
      selectTrade: 'Selecione um trade para ver detalhes',
      calendar: 'Calendário',
      noTradesYet: 'Nenhum trade analisado ainda',
      uploadFirst: 'Envie e analise seu primeiro gráfico para começar',
      showingTradesFor: 'Mostrando trades de',
      clearFilter: 'Limpar filtro',
      notTriggered: 'Não Acionado',
      // Filters and sorting
      sortBy: 'Ordenar por',
      filterBy: 'Filtrar por',
      newest: 'Mais recentes',
      oldest: 'Mais antigos',
      highestRR: 'Maior RR',
      lowestRR: 'Menor RR',
      winsOnly: 'Apenas wins',
      lossesOnly: 'Apenas losses',
      // Trade card labels
      entry: 'Entrada',
      exit: 'Saída',
      stopLoss: 'Stop Loss',
      takeProfit: 'Take Profit',
      notes: 'Anotações',
      setup: 'Setup',
      confidence: 'Confiança',
      outcome: 'Resultado',
      win: 'Win',
      loss: 'Loss',
      open: 'Aberto',
      pending: 'Pendente',
      // Actions
      deleteTrade: 'Excluir Trade',
      editTrade: 'Editar Trade',
      viewChart: 'Ver Gráfico',
    },

    // Analytics
    analytics: {
      title: 'Análises',
      subtitle: 'Análise profunda da sua performance',
      advancedTitle: 'Análises Avançadas',
      advancedSubtitle: 'Análise multidimensional de performance',
      filters: 'Filtros',
      setup: 'Setup',
      allSetups: 'Todos os Setups',
      assetGroup: 'Grupo de Ativos',
      allGroups: 'Todos os Grupos',
      timeframe: 'Timeframe',
      allTimeframes: 'Todos os Timeframes',
      tradeType: 'Tipo de Trade',
      allTypes: 'Todos os Tipos',
      noMatchingTrades: 'Nenhum trade corresponde aos filtros selecionados',
      performanceByTimeframe: 'Performance por Timeframe',
      performanceByAssetGroup: 'Performance por Grupo de Ativos',
      tradeTypeComparison: 'Comparação por Tipo de Trade',
      basedOnSettings: 'Baseado nas suas configurações',
      editThresholds: 'Editar limites',
      noTradesClassified: 'Nenhum trade classificado ainda',
      startAddingTrades: 'Comece adicionando trades com timeframes para ver sua análise de Scalp, Day Trade e Swing.',
      // Section titles
      overallPerformance: 'Performance Geral',
      performanceMetrics: 'Métricas de Performance',
      setupPerformance: 'Performance por Setup',
      assetPerformance: 'Performance por Ativo',
      behaviorAnalysis: 'Análise de Comportamento',
      // Labels
      totalTrades: 'Total de Trades',
      winningTrades: 'Trades Vencedores',
      losingTrades: 'Trades Perdedores',
      avgWin: 'Média de Win',
      avgLoss: 'Média de Loss',
      largestWin: 'Maior Win',
      largestLoss: 'Maior Loss',
      consecutiveWins: 'Wins Consecutivos',
      consecutiveLosses: 'Losses Consecutivos',
      totalR: 'TOTAL R',
      tradesPerMonth: 'operações/mês',
      rPerMonth: 'R/mês',
      monthlyPerformance: 'PERFORMANCE MENSAL',
      monthColumn: 'MÊS',
      operationsColumn: 'OPERAÇÕES',
      currentMonth: 'Mês atual',
      monthlyDrilldownHint: 'Clique em um mês para ver as operações',
      // Empty states
      noTradesInPeriod: 'Nenhum trade neste período',
      adjustFilters: 'Tente ajustar seus filtros',
      noSetupData: 'Sem dados de setup disponíveis',
      noAssetData: 'Sem dados de ativos disponíveis',
    },

    // Trade Types
    tradeTypes: {
      scalp: 'Scalp',
      dayTrade: 'Day Trade',
      swingTrade: 'Swing Trade',
      noScalpTrades: 'Nenhum trade scalp',
      noDayTrades: 'Nenhum day trade',
      noSwingTrades: 'Nenhum swing trade',
      shortestDuration: 'Trades de menor duração',
      mediumDuration: 'Trades de duração média',
      longestDuration: 'Trades de maior duração',
    },

    // Alerts
    alerts: {
      title: 'Alertas',
      subtitle: 'Mantenha suas regras de trading sob controle',
      noAlerts: 'Nenhum alerta configurado',
      createFirst: 'Crie seu primeiro alerta para monitorar limites importantes',
      createAlert: 'Criar Alerta',
      editAlert: 'Editar Alerta',
      alertName: 'Nome do Alerta',
      condition: 'Condição',
      threshold: 'Limite',
      activeAlerts: 'Alertas Ativos',
      triggeredAlerts: 'Alertas Disparados',
      alertHistory: 'Histórico de Alertas',
      riskControl: 'Controle de Risco',
      performanceMonitor: 'Monitor de Performance',
      behaviorTracking: 'Rastreamento de Comportamento',
      // Alert types
      maxDailyLoss: 'Perda Diária Máxima',
      maxWeeklyLoss: 'Perda Semanal Máxima',
      winRateThreshold: 'Limite de Win Rate',
      profitTarget: 'Meta de Lucro',
      maxConsecutiveLosses: 'Máximo de Perdas Consecutivas',
      maxTradesPerDay: 'Máximo de Trades por Dia',
    },

    // Insights
    insights: {
      title: 'Insights',
      recentRRWarning: 'Alerta de RR Recente',
      winningRRWarning: 'Alerta de RR dos Gains',
      losingStreak: 'Sequência de Perdas',
      performanceDropping: 'Sua performance recente está caindo',
      reviewStrategy: 'Revise sua estratégia e considere fazer uma pausa',
      limitedData: 'Dados limitados',
      notEnoughData: 'Não há dados suficientes para gerar uma decisão confiável ainda',
      improvement: 'melhoria',
      decline: 'queda',
      // Additional insight types
      bestSetup: 'Melhor Setup',
      worstSetup: 'Pior Setup',
      strongTimeframe: 'Timeframe Forte',
      weakTimeframe: 'Timeframe Fraco',
      overtrading: 'Alerta de Overtrading',
      goodStreak: 'Boa Sequência',
      badStreak: 'Sequência Ruim',
      consistency: 'Consistência',
      // Descriptions
      yourBestPerforming: 'Seu melhor',
      considerstopping: 'Considere parar por hoje',
      keepItUp: 'Continue assim!',
      takeABreak: 'Considere fazer uma pausa',
    },

    // Settings
    settings: {
      title: 'Configurações',
      tradeClassification: 'Classificação de Trade',
      tradeClassificationDesc: 'Defina como os trades são classificados com base na duração',
      scalpMaxDuration: 'Duração Máxima do Scalp',
      dayTradeMaxDuration: 'Duração Máxima do Day Trade',
      minutes: 'minutos',
      swingTradeNote: 'Trades com duração maior que Day Trade serão classificados como Swing Trade',
      saveChanges: 'Salvar Alterações',
      savingChanges: 'Salvando...',
      changesSaved: 'Configurações salvas com sucesso',
      reclassifyNote: 'Todos os trades existentes serão reclassificados com base nos novos limites',
    },

    // Performance
    performance: {
      title: 'Performance',
      subtitle: 'Acompanhe seu crescimento ao longo do tempo',
      equityCurve: 'Curva de Equity',
      monthlyBreakdown: 'Análise Mensal',
      weeklyBreakdown: 'Análise Semanal',
      dailyBreakdown: 'Análise Diária',
      tradingDays: 'Dias de Trading',
      profitableDays: 'Dias Lucrativos',
      losingDays: 'Dias de Perda',
      breakEvenDays: 'Dias no Zero',
      // Additional
      cumulativeReturn: 'Retorno Acumulado',
      dailyReturn: 'Retorno Diário',
      weeklyReturn: 'Retorno Semanal',
      monthlyReturn: 'Retorno Mensal',
      maxDrawdown: 'Drawdown Máximo',
      currentDrawdown: 'Drawdown Atual',
      recoveryFactor: 'Fator de Recuperação',
      sharpeRatio: 'Sharpe Ratio',
      // Time periods
      last7Days: 'Últimos 7 Dias',
      last30Days: 'Últimos 30 Dias',
      last90Days: 'Últimos 90 Dias',
      allTime: 'Todo o Período',
      thisMonth: 'Este Mês',
      thisWeek: 'Esta Semana',
      // Stats
      bestMonth: 'Melhor Mês',
      worstMonth: 'Pior Mês',
      avgMonthlyReturn: 'Retorno Mensal Médio',
      positiveMonths: 'Meses Positivos',
      negativeMonths: 'Meses Negativos',
    },

    // Behavior Analytics
    behavior: {
      partialExecution: 'Execução Parcial',
      partialAnalysis: 'Análise de Parciais',
      plannedTrades: 'Trades Planejados',
      executionQuality: 'Qualidade de Execução',
      notTriggeredTrades: 'Trades Não Acionados',
      followedPlan: 'Seguiu o Plano',
      holdTime: 'Tempo de Holding',
      avgHoldTime: 'Tempo Médio de Holding',
      expectedHoldTime: 'Tempo Esperado de Holding',
      partialDecision: 'Parcial vs Não-Parcial',
      givenBack: 'Devolvido',
      firstPartial: 'Primeiro Parcial',
      finalRealized: 'Final Realizado',
    },

    // Errors & Messages
    messages: {
      error: 'Erro',
      success: 'Sucesso',
      warning: 'Atenção',
      info: 'Info',
      somethingWentWrong: 'Algo deu errado',
      tryAgain: 'Por favor, tente novamente',
      savedSuccessfully: 'Salvo com sucesso',
      deletedSuccessfully: 'Excluído com sucesso',
      loadingFailed: 'Falha ao carregar dados',
      connectionError: 'Erro de conexão',
      sessionExpired: 'Sessão expirada',
      pleaseLogin: 'Por favor, faça login para continuar',
      confirmDelete: 'Tem certeza que deseja excluir?',
      actionCannotBeUndone: 'Esta ação não pode ser desfeita',
    },

    // Pro/Upgrade
    pro: {
      becomePro: 'Seja Pro',
      unlockFeatures: 'Desbloqueie todas as funcionalidades',
      currentPlan: 'Plano Atual',
      freePlan: 'Plano Gratuito',
      proPlan: 'Plano Pro',
      tradeLimit: 'Limite de Trades',
      unlimited: 'Ilimitado',
      upgradeNow: 'Upgrade Agora',
      monthlyPlan: 'Mensal',
      yearlyPlan: 'Anual',
      perMonth: '/mês',
      perYear: '/ano',
    },

    // Footer
    footer: {
      builtBy: 'Desenvolvido por',
      allRightsReserved: 'Todos os direitos reservados',
    },
  },
};

// Supported languages configuration
// Only 'en' and 'ptBR' have actual translations - others are placeholders for future
export const languages = [
  { code: 'en', name: 'English', shortName: 'EN', available: true },
  { code: 'ptBR', name: 'Português', shortName: 'PT-BR', available: true },
  { code: 'ko', name: '한국어', shortName: 'KO', available: false },
  { code: 'he', name: 'עברית', shortName: 'HE', available: false },
  { code: 'vi', name: 'Tiếng Việt', shortName: 'VI', available: false },
  { code: 'ru', name: 'Русский', shortName: 'RU', available: false },
];

// Languages that have actual translations
export const availableLanguages = ['en', 'ptBR'];

// Default language
export const defaultLanguage = 'en';

// Local storage key
export const LANGUAGE_STORAGE_KEY = 'geni_language';

/**
 * Get nested translation value from object path
 * @param {Object} obj - Translation object
 * @param {string} path - Dot notation path like 'menu.dashboard'
 * @returns {string} Translation value or path if not found
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Create translation function for a specific language
 * @param {string} lang - Language code ('en' or 'ptBR')
 * @returns {Function} Translation function
 */
export function createTranslator(lang) {
  const currentTranslations = translations[lang] || translations[defaultLanguage];
  const fallbackTranslations = translations[defaultLanguage];

  return function t(key, fallback) {
    // Try current language first
    let value = getNestedValue(currentTranslations, key);
    
    // Fall back to English if not found
    if (value === undefined) {
      value = getNestedValue(fallbackTranslations, key);
    }
    
    // Return fallback or key if still not found
    if (value === undefined) {
      return fallback || key;
    }
    
    return value;
  };
}
